import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { 
  Target, ShoppingBag, Sparkles, Settings, Lightbulb, 
  Briefcase, Megaphone, GraduationCap, Smile, Heart, 
  Gem, Zap, Globe, Film, Laugh, MinusSquare, Clock, 
  BookOpen, Rewind, Check, ChevronDown, ChevronUp 
} from "lucide-react";

// Intent options with their suggested styles
const intentOptions = [
  { 
    id: "promotional", 
    label: "Promotional", 
    icon: Target, 
    selectedClass: "bg-viraly-orange/20 border-viraly-orange ring-1 ring-viraly-orange/30",
    iconClass: "text-viraly-orange",
    dotClass: "bg-viraly-orange",
    placeholder: "e.g., Highlight the discount, create urgency, focus on the main offer...",
    suggestedStyles: ["promotional", "urgent", "energetic"]
  },
  { 
    id: "showcase", 
    label: "Showcase", 
    icon: ShoppingBag, 
    selectedClass: "bg-blue-500/20 border-blue-500 ring-1 ring-blue-500/30",
    iconClass: "text-blue-500",
    dotClass: "bg-blue-500",
    placeholder: "e.g., Feature the product details, show the key benefits...",
    suggestedStyles: ["professional", "luxury", "documentary"]
  },
  { 
    id: "brand", 
    label: "Brand Awareness", 
    icon: Sparkles, 
    selectedClass: "bg-purple-500/20 border-purple-500 ring-1 ring-purple-500/30",
    iconClass: "text-purple-500",
    dotClass: "bg-purple-500",
    placeholder: "e.g., Emphasize company values, build trust, share the story...",
    suggestedStyles: ["inspirational", "storytelling", "professional"]
  },
  { 
    id: "educate", 
    label: "Educate", 
    icon: GraduationCap, 
    selectedClass: "bg-emerald-500/20 border-emerald-500 ring-1 ring-emerald-500/30",
    iconClass: "text-emerald-500",
    dotClass: "bg-emerald-500",
    placeholder: "e.g., Explain the features, teach the audience, provide tips...",
    suggestedStyles: ["educational", "professional", "minimalist"]
  },
  { 
    id: "engage", 
    label: "Engage", 
    icon: Heart, 
    selectedClass: "bg-pink-500/20 border-pink-500 ring-1 ring-pink-500/30",
    iconClass: "text-pink-500",
    dotClass: "bg-pink-500",
    placeholder: "e.g., Start a conversation, ask questions, build community...",
    suggestedStyles: ["casual", "humorous", "energetic"]
  },
  { 
    id: "custom", 
    label: "Custom", 
    icon: Settings, 
    selectedClass: "bg-gray-500/20 border-gray-400 ring-1 ring-gray-400/30",
    iconClass: "text-gray-400",
    dotClass: "bg-gray-400",
    placeholder: "e.g., Create specific content for my Christmas campaign...",
    suggestedStyles: [] // All styles available
  },
];

// Style options with icons
const styleOptions = {
  professional: { name: "Professional", icon: Briefcase, color: "blue" },
  promotional: { name: "Promotional", icon: Megaphone, color: "orange" },
  educational: { name: "Educational", icon: GraduationCap, color: "emerald" },
  inspirational: { name: "Inspirational", icon: Sparkles, color: "purple" },
  casual: { name: "Casual", icon: Smile, color: "yellow" },
  romantic: { name: "Romantic", icon: Heart, color: "pink" },
  luxury: { name: "Luxury", icon: Gem, color: "amber" },
  energetic: { name: "Energetic", icon: Zap, color: "red" },
  cultural: { name: "Cultural", icon: Globe, color: "teal" },
  documentary: { name: "Documentary", icon: Film, color: "slate" },
  humorous: { name: "Humorous", icon: Laugh, color: "lime" },
  minimalist: { name: "Minimalist", icon: MinusSquare, color: "gray" },
  urgent: { name: "Urgent", icon: Clock, color: "red" },
  storytelling: { name: "Storytelling", icon: BookOpen, color: "indigo" },
  nostalgic: { name: "Nostalgic", icon: Rewind, color: "amber" },
};

const colorClasses = {
  blue: { bg: "bg-blue-500/20", border: "border-blue-500", text: "text-blue-400" },
  orange: { bg: "bg-orange-500/20", border: "border-orange-500", text: "text-orange-400" },
  emerald: { bg: "bg-emerald-500/20", border: "border-emerald-500", text: "text-emerald-400" },
  purple: { bg: "bg-purple-500/20", border: "border-purple-500", text: "text-purple-400" },
  yellow: { bg: "bg-yellow-500/20", border: "border-yellow-500", text: "text-yellow-400" },
  pink: { bg: "bg-pink-500/20", border: "border-pink-500", text: "text-pink-400" },
  amber: { bg: "bg-amber-500/20", border: "border-amber-500", text: "text-amber-400" },
  red: { bg: "bg-red-500/20", border: "border-red-500", text: "text-red-400" },
  teal: { bg: "bg-teal-500/20", border: "border-teal-500", text: "text-teal-400" },
  slate: { bg: "bg-slate-500/20", border: "border-slate-400", text: "text-slate-400" },
  lime: { bg: "bg-lime-500/20", border: "border-lime-500", text: "text-lime-400" },
  gray: { bg: "bg-gray-500/20", border: "border-gray-400", text: "text-gray-400" },
  indigo: { bg: "bg-indigo-500/20", border: "border-indigo-500", text: "text-indigo-400" },
};

export const IntentAndStyleSelector = ({ 
  intent, 
  onIntentChange, 
  contentStyle,
  onStyleChange,
  context, 
  onContextChange,
  onShowListingDetails,
  inputType = "url" // "url", "text", "image"
}) => {
  const [showAllStyles, setShowAllStyles] = useState(false);
  const selectedIntent = intentOptions.find(i => i.id === intent) || intentOptions[0];
  
  // Get suggested styles based on intent
  const suggestedStyleIds = selectedIntent.suggestedStyles || [];
  const allStyleIds = Object.keys(styleOptions);
  
  // Separate suggested and other styles
  const suggestedStyles = suggestedStyleIds.map(id => ({ id, ...styleOptions[id] }));
  const otherStyles = allStyleIds
    .filter(id => !suggestedStyleIds.includes(id))
    .map(id => ({ id, ...styleOptions[id] }));
  
  // Auto-select first suggested style when intent changes
  useEffect(() => {
    if (suggestedStyleIds.length > 0) {
      // Always select the first suggested style for the new intent
      onStyleChange(suggestedStyleIds[0]);
    }
  }, [intent]); // Only depend on intent to trigger on intent change
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Section 1: Goal/Intent Selection */}
      <div className="p-5 rounded-xl bg-white/5 border border-white/10 space-y-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-viraly-orange" />
          <h4 className="font-medium text-viraly-light">What's your goal?</h4>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {intentOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = intent === option.id;
            
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onIntentChange(option.id);
                  if (option.id === "custom" && onShowListingDetails) {
                    onShowListingDetails(true);
                  }
                }}
                className={`p-3 rounded-lg text-left transition-all flex items-center gap-2 ${
                  isSelected
                    ? option.selectedClass
                    : "bg-white/5 border-white/10 border hover:bg-white/10"
                }`}
                data-testid={`intent-${option.id}`}
              >
                <Icon className={`w-4 h-4 ${isSelected ? option.iconClass : 'text-viraly-muted'}`} />
                <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-viraly-light'}`}>
                  {option.label}
                </span>
                {isSelected && (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className={`ml-auto w-2 h-2 rounded-full ${option.dotClass}`}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Section 2: Visual Style - Based on Intent */}
      <div className="p-5 rounded-xl bg-white/5 border border-white/10 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <h4 className="font-medium text-viraly-light">Visual Style</h4>
          </div>
          {suggestedStyles.length > 0 && (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
              AI Suggested
            </span>
          )}
        </div>
        
        {/* Suggested Styles */}
        {suggestedStyles.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-viraly-muted">Recommended for {selectedIntent.label}:</p>
            <div className="grid grid-cols-3 gap-2">
              {suggestedStyles.map((style) => {
                const Icon = style.icon;
                const isSelected = contentStyle === style.id;
                const colors = colorClasses[style.color] || colorClasses.gray;
                
                return (
                  <motion.button
                    key={style.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onStyleChange(style.id)}
                    className={`relative p-3 rounded-xl border-2 text-left transition-all ${
                      isSelected 
                        ? `${colors.bg} ${colors.border}` 
                        : "border-white/10 bg-white/5 hover:border-white/20"
                    }`}
                    data-testid={`style-${style.id}`}
                  >
                    {isSelected && (
                      <div className={`absolute top-2 right-2 w-4 h-4 ${colors.bg} rounded-full flex items-center justify-center`}>
                        <Check className={`w-2.5 h-2.5 ${colors.text}`} />
                      </div>
                    )}
                    <Icon className={`w-5 h-5 mb-1 ${isSelected ? colors.text : 'text-viraly-muted'}`} />
                    <p className={`text-sm font-medium ${isSelected ? colors.text : 'text-viraly-light'}`}>
                      {style.name}
                    </p>
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Other Styles - Collapsible */}
        {otherStyles.length > 0 && (
          <div className="pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={() => setShowAllStyles(!showAllStyles)}
              className="flex items-center gap-2 text-sm text-viraly-muted hover:text-viraly-light transition-colors w-full"
            >
              {showAllStyles ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              {showAllStyles ? "Hide other styles" : `Browse ${otherStyles.length} more styles`}
            </button>
            
            <AnimatePresence>
              {showAllStyles && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2"
                >
                  {otherStyles.map((style) => {
                    const Icon = style.icon;
                    const isSelected = contentStyle === style.id;
                    const colors = colorClasses[style.color] || colorClasses.gray;
                    
                    return (
                      <motion.button
                        key={style.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => onStyleChange(style.id)}
                        className={`relative p-2 rounded-lg border-2 text-left transition-all ${
                          isSelected 
                            ? `${colors.bg} ${colors.border}` 
                            : "border-white/10 bg-white/5 hover:border-white/20"
                        }`}
                        data-testid={`style-${style.id}`}
                      >
                        <Icon className={`w-4 h-4 mb-0.5 ${isSelected ? colors.text : 'text-viraly-muted'}`} />
                        <p className={`text-xs font-medium ${isSelected ? colors.text : 'text-viraly-light'}`}>
                          {style.name}
                        </p>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      
      {/* Section 3: Context/Vision Input */}
      <div className="p-5 rounded-xl bg-white/5 border border-white/10 space-y-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-viraly-orange" />
          <label className="text-sm font-medium text-viraly-light">
            Add Your Vision <span className="text-viraly-muted font-normal">(Tell AI What You Want)</span>
          </label>
        </div>
        
        <Textarea
          value={context}
          onChange={(e) => onContextChange(e.target.value)}
          placeholder={selectedIntent.placeholder}
          className="min-h-[80px] bg-white/5 border-white/10 text-viraly-light placeholder:text-viraly-muted/60 focus:border-viraly-orange/50 focus:ring-viraly-orange/20 resize-none text-sm"
          data-testid="vision-context-input"
        />
        
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-viraly-muted/60">
            The more you share, the closer we get to perfection
          </p>
          {context && context.length > 0 && (
            <span className="text-[10px] text-viraly-muted/50">
              {context.length} chars
            </span>
          )}
        </div>
        
        {/* Quick suggestion chips */}
        {(!context || context.length === 0) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {[
              "Focus on the main product",
              "Make it festive/seasonal",
              "Emphasize the discount",
              "Keep it minimal"
            ].map((suggestion, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onContextChange(suggestion)}
                className="text-[10px] px-2 py-1 rounded-full bg-white/5 text-viraly-muted hover:bg-viraly-orange/10 hover:text-viraly-orange transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default IntentAndStyleSelector;
